import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu, X, ShoppingCart, ChevronDown, ChevronRight,
  Plane, Ship, Truck, Package, Briefcase,
  FileText, ClipboardList, Calculator, Boxes, Receipt, BookOpen,
  ArrowUpRight, Search,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";
import { useBranding } from "../context/BrandingContext";
import LanguageSwitcher from "./LanguageSwitcher";
import useSiteContent, { findBlock } from "../hooks/useSiteContent";
import { sanitizeNavUrl } from "../lib/urlSafe";

/* ----------------------------------------------------------------------
 *  Header — Release 1.C
 *  Compact 7-item primary navigation with two grouped dropdowns
 *  (Services + Tools & Forms). Adds scroll-aware glassmorphism + premium
 *  dropdown panels + premium mobile drawer. All link URLs flow through
 *  sanitizeNavUrl so /admin, /api, javascript:, data:, vbscript:, file:,
 *  ftp: cannot be injected via the CMS overlay.
 * ---------------------------------------------------------------------- */

const SERVICE_ITEMS = [
  { to: "/luchtvracht",           i18nKey: "luchtvracht",     icon: Plane },
  { to: "/zeevracht",             i18nKey: "zeevracht",       icon: Ship },
  { to: "/shop-drop-ship",        i18nKey: "shop_drop_ship",  icon: Package },
  { to: "/pickup-delivery",       i18nKey: "pickup",          icon: Truck },
  { to: "/verhuizing",            i18nKey: "verhuizing",      icon: Boxes },
  { to: "/zakelijk",              i18nKey: "zakelijk",        icon: Briefcase },
];

const TOOL_ITEMS = [
  { to: "/tracking",                  i18nKey: "tracking",        icon: Search },
  { to: "/documenten",                i18nKey: "documenten",      icon: FileText },
  { to: "/factuur-maken",             i18nKey: "factuur",         icon: Receipt },
  { to: "/paklijst",                  i18nKey: "paklijst",        icon: ClipboardList },
  { to: "/boekingsformulier",         i18nKey: "boeking",         icon: BookOpen },
  { to: "/btw-terugvragen",           i18nKey: "btw",             icon: Receipt },
  { to: "/volumemeter",               i18nKey: "volumemeter",     icon: Calculator },
  { to: "/cbm-calculator",            i18nKey: "cbm_calculator",  icon: Calculator },
  { to: "/pickup-calculator",         i18nKey: "pickup_calc",     icon: Truck },
];

// =====================================================================
// Helpers
// =====================================================================

function NavTrigger({ open, onToggle, label, testid }) {
  return (
    <button
      type="button"
      aria-haspopup="true"
      aria-expanded={open}
      onClick={onToggle}
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-3 py-2 text-[13px] font-chivo font-bold tracking-wide uppercase whitespace-nowrap rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 ${
        open ? "text-brand-900 bg-slate-100" : "text-ink-700 hover:text-brand-900 hover:bg-slate-50"
      }`}
    >
      {label}
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
    </button>
  );
}

function DesktopDropdown({ items, testidPrefix, dictKey, t }) {
  return (
    <div
      role="menu"
      // top-full negates the gap to the trigger; pt-3 keeps the visual
      // gap while the invisible bridge prevents hover-out flicker.
      className="absolute top-full left-0 pt-3 w-[26rem] z-50 animate-fade-up"
      data-testid={`${testidPrefix}-menu`}
    >
      <div className="rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-xl shadow-brand-card p-2.5">
        {items.map((s) => {
          const safeTo = sanitizeNavUrl(s.to);
          if (!safeTo) return null;
          const slug = s.to.replace(/^\//, "").replace(/\W+/g, "-");
          const label = t(`${dictKey}.${s.i18nKey}_label`);
          const desc = t(`${dictKey}.${s.i18nKey}_desc`);
          return (
            <Link
              key={s.to}
              to={safeTo}
              role="menuitem"
              data-testid={`${testidPrefix}-${slug}`}
              className="group flex items-start gap-3 px-4 py-3.5 rounded-xl hover:bg-brand-50 focus:bg-brand-50 outline-none transition-all"
            >
              <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand-500/10 group-hover:bg-brand-500 text-brand-500 group-hover:text-white flex items-center justify-center transition-colors">
                <s.icon className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-chivo font-bold text-brand-900 text-[14px] flex items-center gap-1">
                  {label}
                  <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-brand-500" />
                </div>
                {desc && <div className="text-[12px] text-ink-400 leading-snug mt-0.5">{desc}</div>}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function MobileAccordion({ label, testid, items, onItemClick, prefix, dictKey, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100" data-testid={`${testid}-wrap`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testid}
        className="w-full py-4 flex items-center justify-between font-chivo font-bold uppercase text-sm text-brand-900"
      >
        <span>{label}</span>
        <ChevronRight className={`w-4 h-4 transition-transform text-brand-500 ${open ? "rotate-90" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="pb-3 pl-1 space-y-1" data-testid={`${testid}-panel`}>
          {items.map((s) => {
            const safeTo = sanitizeNavUrl(s.to);
            if (!safeTo) return null;
            const slug = s.to.replace(/^\//, "").replace(/\W+/g, "-");
            const itemLabel = t(`${dictKey}.${s.i18nKey}_label`);
            return (
              <NavLink
                key={s.to}
                to={safeTo}
                onClick={() => { setOpen(false); onItemClick(); }}
                className="flex items-center gap-3 py-2.5 px-3 rounded-lg text-[14px] font-chivo font-semibold text-ink-700 hover:bg-brand-50 hover:text-brand-900"
                data-testid={`${prefix}-${slug}`}
              >
                <s.icon className="w-4 h-4 text-brand-500" strokeWidth={1.75} />
                {itemLabel}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Header
// =====================================================================

export default function Header() {
  const [open, setOpen] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totals } = useCart();
  const { t } = useI18n();
  const { logoUrl, logoAlt } = useBranding();
  const location = useLocation();
  const servicesRef = useRef(null);
  const toolsRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Open one dropdown, close the other, and cancel any pending close timer.
  const openMenu = (menu) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (menu === "services") { setShowServices(true); setShowTools(false); }
    else if (menu === "tools") { setShowTools(true); setShowServices(false); }
  };
  // Schedule a close with a comfortable 350ms grace period so the cursor
  // can cross from the trigger label to the dropdown panel without the
  // menu snapping shut.
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setShowServices(false);
      setShowTools(false);
      closeTimerRef.current = null;
    }, 350);
  };
  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // CMS overlay — strictly sanitised. Anything that fails sanitiser falls
  // back to the hardcoded safe defaults below.
  const { blocks } = useSiteContent("header");
  const navBlock = findBlock(blocks, "navigation_links")?.data || {};
  const myAccountLabel = "Mijn 5999Cargo";
  const myAccountUrl = "/mijn-5999cargo";

  // Close dropdowns on route change so the menu doesn't linger
  useEffect(() => {
    setShowServices(false);
    setShowTools(false);
    setOpen(false);
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, [location.pathname]);

  // Close dropdowns on outside click + Escape (keyboard a11y)
  useEffect(() => {
    function onDoc(e) {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setShowServices(false);
      }
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setShowTools(false);
      }
    }
    function onEsc(e) {
      if (e.key === "Escape") {
        setShowServices(false);
        setShowTools(false);
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // Cleanup timer on unmount
  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  // Scroll-aware glass intensity
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `px-3 py-2 text-[13px] font-chivo font-bold tracking-wide uppercase whitespace-nowrap rounded-lg transition-colors relative ${
      isActive
        ? "text-brand-900 bg-slate-100"
        : "text-ink-700 hover:text-brand-900 hover:bg-slate-50"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-brand-soft"
          : "bg-white/70 backdrop-blur-xl border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20 gap-4">
        {/* Logo — uses BrandingProvider with hardcoded fallback */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group flex-shrink-0">
          <img
            src={logoUrl}
            alt={logoAlt}
            className="h-12 lg:h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Hoofdmenu">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">
            {t("nav.home")}
          </NavLink>

          <div
            ref={servicesRef}
            className="relative"
            onMouseEnter={() => openMenu("services")}
            onMouseLeave={scheduleClose}
          >
            <NavTrigger
              open={showServices}
              onToggle={() => { setShowServices((v) => !v); setShowTools(false); cancelClose(); }}
              label={t("nav.services")}
              testid="nav-services-toggle"
            />
            {showServices && (
              <div onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                <DesktopDropdown items={SERVICE_ITEMS} testidPrefix="nav-service" dictKey="service_items" t={t} />
              </div>
            )}
          </div>

          <div
            ref={toolsRef}
            className="relative"
            onMouseEnter={() => openMenu("tools")}
            onMouseLeave={scheduleClose}
          >
            <NavTrigger
              open={showTools}
              onToggle={() => { setShowTools((v) => !v); setShowServices(false); cancelClose(); }}
              label={t("nav.tools")}
              testid="nav-tools-toggle"
            />
            {showTools && (
              <div onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                <DesktopDropdown items={TOOL_ITEMS} testidPrefix="nav-tool" dictKey="tool_items" t={t} />
              </div>
            )}
          </div>

          <NavLink to="/webshop" className={linkCls} data-testid="nav-webshop">
            {t("nav.webshop")}
          </NavLink>
          <NavLink to="/online-betalen" className={linkCls} data-testid="nav-pay-online">
            {t("nav.pay_online")}
          </NavLink>
          <NavLink to="/word-agent" className={linkCls} data-testid="nav-word-agent">
            {t("nav.become_agent")}
          </NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">
            {t("nav.contact")}
          </NavLink>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="hidden sm:block">
            <LanguageSwitcher compact />
          </div>
          <Link
            to="/webshop"
            data-testid="cart-link"
            className="relative p-2.5 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label={t("nav.cart")}
          >
            <ShoppingCart className="w-5 h-5 text-brand-900" strokeWidth={1.75} />
            {totals.count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-brand-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shadow-sm"
              >
                {totals.count}
              </span>
            )}
          </Link>
          <NavLink
            to={myAccountUrl}
            data-testid="nav-my5999cargo"
            className={({ isActive }) =>
              `hidden lg:inline-flex items-center gap-1 px-4 py-2 rounded-xl text-[12px] font-chivo font-bold tracking-wide uppercase whitespace-nowrap transition-all ${
                isActive
                  ? "bg-brand-900 text-white"
                  : "bg-white border border-slate-200 text-brand-900 hover:border-brand-500 hover:bg-brand-50"
              }`
            }
          >
            {myAccountLabel}
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.25} />
          </NavLink>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2.5 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label={open ? "Sluit menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5 text-brand-900" /> : <Menu className="w-5 h-5 text-brand-900" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-5rem)] overflow-y-auto"
          data-testid="mobile-menu-panel"
        >
          <div className="px-4 py-3 flex flex-col">
            <NavLink
              to="/"
              end
              onClick={() => setOpen(false)}
              className="py-4 font-chivo font-bold uppercase text-sm text-brand-900 border-b border-slate-100"
              data-testid="m-nav-home"
            >
              {t("nav.home")}
            </NavLink>

            <MobileAccordion
              label={t("nav.services")}
              testid="m-nav-services"
              prefix="m-nav-service"
              items={SERVICE_ITEMS}
              dictKey="service_items"
              t={t}
              onItemClick={() => setOpen(false)}
            />
            <MobileAccordion
              label={t("nav.tools")}
              testid="m-nav-tools"
              prefix="m-nav-tool"
              items={TOOL_ITEMS}
              dictKey="tool_items"
              t={t}
              onItemClick={() => setOpen(false)}
            />

            <NavLink to="/webshop" onClick={() => setOpen(false)} className="py-4 font-chivo font-bold uppercase text-sm text-brand-900 border-b border-slate-100" data-testid="m-nav-webshop">{t("nav.webshop")}</NavLink>
            <NavLink to="/online-betalen" onClick={() => setOpen(false)} className="py-4 font-chivo font-bold uppercase text-sm text-brand-900 border-b border-slate-100" data-testid="m-nav-pay-online">{t("nav.pay_online")}</NavLink>
            <NavLink to="/word-agent" onClick={() => setOpen(false)} className="py-4 font-chivo font-bold uppercase text-sm text-brand-900 border-b border-slate-100" data-testid="m-nav-word-agent">{t("nav.become_agent")}</NavLink>
            <NavLink to="/contact" onClick={() => setOpen(false)} className="py-4 font-chivo font-bold uppercase text-sm text-brand-900 border-b border-slate-100" data-testid="m-nav-contact">{t("nav.contact")}</NavLink>

            <NavLink
              to={myAccountUrl}
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-900 text-white font-chivo font-bold uppercase text-sm tracking-wide hover:bg-brand-700"
              data-testid="m-nav-my5999cargo"
            >
              {myAccountLabel}
              <ArrowUpRight className="w-4 h-4" />
            </NavLink>

            <div className="py-3 border-t border-slate-100 mt-4 flex items-center justify-between">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
